import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import toast from 'react-hot-toast';
import { useNotificationPreferences } from '@/api/notifications/use-notification-preferences';
import {
    Bell,
    Mail,
    MessageCircle,
    Monitor,
    Globe,
    CheckCircle,
    AlertCircle,
    Loader2,
    Settings,
} from 'lucide-react';

const NotificationPreferencesCard: React.FC = () => {
    const { t } = useTranslation('profile');
    const {
        preferences,
        channels,
        languages,
        loading,
        error,
        updating,
        updatePreferences,
        testNotification,
        clearError,
    } = useNotificationPreferences();

    const [testingChannel, setTestingChannel] = useState<string | null>(null);

    const handleLanguageChange = async (language: string) => {
        try {
            await updatePreferences({ preferred_language: language });
            toast.success(t('languageUpdatedSuccess'));
        } catch (error) {
            toast.error(t('failedToUpdateLanguage'));
        }
    };

    const handleChannelToggle = async (channel: string, enabled: boolean) => {
        if (!preferences) return;

        const currentChannels = preferences.preferred_channels || [];
        let newChannels: string[];

        if (enabled) {
            newChannels = [...currentChannels, channel];
        } else {
            newChannels = currentChannels.filter(c => c !== channel);
        }

        try {
            await updatePreferences({ preferred_channels: newChannels });
            toast.success(t('notificationChannelsUpdated'));
        } catch (error) {
            toast.error(t('failedToUpdateChannels'));
        }
    };

    const handleTestNotification = async (channel: string) => {
        try {
            setTestingChannel(channel);
            await testNotification(channel);
            toast.success(t('testNotificationSentVia', { channel }));
        } catch (error) {
            toast.error(t('failedToSendTestVia', { channel }));
        } finally {
            setTestingChannel(null);
        }
    };

    const getChannelIcon = (channelId: string) => {
        switch (channelId) {
            case 'email':
                return <Mail className="h-4 w-4" />;
            case 'sms':
                return <MessageCircle className="h-4 w-4" />;
            case 'push':
                return <Bell className="h-4 w-4" />;
            case 'in_app':
                return <Monitor className="h-4 w-4" />;
            default:
                return <Bell className="h-4 w-4" />;
        }
    };

    const channelLabelKeys: Record<string, string> = {
        email: 'channelEmail',
        sms: 'channelSms',
        push: 'channelPush',
        in_app: 'channelInApp',
    };
    const getChannelName = (channelId: string) => {
        const channel = channels.find(c => c.id === channelId);
        if (channel?.name) return channel.name;
        const key = channelLabelKeys[channelId];
        return key ? t(key) : channelId;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">{t('loadingPreferences')}</span>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                    {error}
                    <Button variant="link" className="p-0 h-auto ml-2" onClick={clearError}>
                        {t('tryAgain')}
                    </Button>
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-4">
            {/* Language Preference */}
            <div className="space-y-2">
                <label className="text-sm font-medium">{t('preferredLanguage')}</label>
                <Select
                    value={preferences?.preferred_language || 'en'}
                    onValueChange={handleLanguageChange}
                    disabled={updating}
                >
                    <SelectTrigger>
                        <SelectValue placeholder={t('selectLanguage')} />
                    </SelectTrigger>
                    <SelectContent>
                        {languages.map(language => (
                            <SelectItem key={language.code} value={language.code}>
                                <div className="flex items-center space-x-2">
                                    <span>{language.native_name}</span>
                                    <Badge variant="secondary" className="text-xs">
                                        {language.name}
                                    </Badge>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Channel Preferences */}
            <div className="space-y-3">
                <label className="text-sm font-medium">{t('notificationChannels')}</label>
                {channels.map(channel => {
                    const isEnabled =
                        preferences?.preferred_channels?.includes(channel.id) || false;

                    return (
                        <div
                            key={channel.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                        >
                            <div className="flex items-center space-x-3">
                                {getChannelIcon(channel.id)}
                                <div>
                                    <p className="font-medium text-sm">{channel.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {channel.description}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Switch
                                    checked={isEnabled}
                                    onCheckedChange={checked =>
                                        handleChannelToggle(channel.id, checked)
                                    }
                                    disabled={updating}
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleTestNotification(channel.id)}
                                    disabled={
                                        !isEnabled || testingChannel === channel.id || updating
                                    }
                                >
                                    {testingChannel === channel.id ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                        <CheckCircle className="h-3 w-3" />
                                    )}
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Current Preferences Summary */}
            <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium">{t('currentSettings')}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                            <Badge variant="outline" className="text-xs">
                                {languages.find(l => l.code === preferences?.preferred_language)
                                    ?.native_name || 'English'}
                            </Badge>
                            {preferences?.preferred_channels?.map(channel => (
                                <Badge key={channel} variant="secondary" className="text-xs">
                                    {getChannelIcon(channel)}
                                    <span className="ml-1">{getChannelName(channel)}</span>
                                </Badge>
                            )) || (
                                <span className="text-xs text-muted-foreground">
                                    {t('noChannelsSelected')}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationPreferencesCard;
